/**
 * hub 页头 hero 带(2026-07-23 卡片重构 R6)。
 *
 * ★为什么不改 `shared/ui/PageHeader`:它是全站共享件,几十个页面在用,为 4 个 hub 页
 * 改它等于给全站改版。hero 做成独立一条带,插在页头与卡墙之间,零外溢。
 *
 * ★左侧文案**全部从 catalog 现算,一个字都不新造**:
 *   - 「这里能出」列的是本 hub 全部卡的**产物形制**去重后的集合(方图/组图/长图/宽图/…)
 *   - 场景数同样现算
 * 这一条既回答了用户进 hub 后的第一个问题(这儿到底能给我出什么),又把卡面刚引入的
 * 「形制」词汇在页头先教一遍 —— 用户往下看卡时,左下角那个几何图示就已经认识了。
 * 写死一段营销话术是更省事的做法,但那既会与卡片脱节,也会随加卡漂移(部门卡副标的
 * 「3 个场景」写死了两轮没人改,就是前车之鉴)。
 *
 * ★人物图靠左侧渐隐融进卡底(mask-image),不是贴一张方图 —— 素材四周本来就是柔化
 * 淡出到皮肤 `--card` 真值的,两者叠起来看不出边界。
 */
import { useActiveSkin } from "@/shared/hooks/useTheme";
import { heroFigureOf } from "../model/figures";
import { SHAPE_META } from "../model/shapes";
import type { SceneCard, SceneHub, SceneShape } from "../model/types";
import { SceneShapeGlyph } from "./SceneShapeGlyph";

interface Props {
  hub: SceneHub;
  /**
   * 本 hub **已过权限门**的卡 —— ★必须由调用方(HubSections)传进来,不能在这里自己数
   * `SCENE_CARDS`。跨模块导流卡(追爆/仿拍/从爆款开始)带 moduleKey,没有
   * `ai_explosive_parse` 的用户在卡墙里看不到它们;hero 若自己数全量,就会出现
   * 「概览说 4 个场景、下面只有 2 张卡」——概览反而比卡墙更不可信。
   * 权限判定在 features 层(useModuleVisible),entities 不能反向依赖,故走 props。
   */
  cards: SceneCard[];
  /** 无场景卡的 hub(如情报枢纽)用它替代「这里能出」那行。 */
  fallbackLine?: string;
}

export function SceneHubHero({ hub, cards, fallbackLine }: Props) {
  // 真源 = <html data-skin>,切皮肤自动换人(与卡片人物同一条路)
  const skin = useActiveSkin();
  // 去重保序:卡在 catalog 里的顺序就是产品想让用户看到的顺序
  const shapes = cards.reduce<SceneShape[]>(
    (acc, c) => (acc.includes(c.shape) ? acc : [...acc, c.shape]),
    []
  );

  return (
    <section
      data-scene-hero={hub}
      className="scene-hero relative overflow-hidden rounded-[calc(var(--radius)+4px)] border bg-card"
    >
      {/* 两端对齐:形制靠左、场景数靠右 —— 全宽下(1616px)左右各据一端时中间会空出
          近 950px,band 读起来是空的。把计数行推到人物图前面,内容自然铺开。 */}
      <div className="scene-hero-body relative z-10 flex items-center justify-between gap-6 px-4 py-3.5">
        <div>
        <div className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
          {shapes.length ? "这里能出" : "能力概览"}
        </div>
        {shapes.length ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-2">
            {shapes.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 text-2xs font-medium text-card-foreground"
              >
                <SceneShapeGlyph shape={s} />
                {SHAPE_META[s].label}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-[12px] text-card-foreground">{fallbackLine}</p>
        )}
        </div>
        {!!cards.length && (
          <div className="shrink-0 text-right">
            <div className="text-lg font-semibold tabular-nums text-card-foreground">
              {cards.length}
            </div>
            <div className="text-2xs leading-tight text-muted-foreground">
              个场景
              <br />
              点卡带预设进创建页
            </div>
          </div>
        )}
      </div>
      <div
        className="scene-hero-fig absolute inset-y-0 right-0 w-[248px]"
        style={{ backgroundImage: `url(${heroFigureOf(skin, hub)})` }}
        aria-hidden
      />
    </section>
  );
}
